<template>
  <div class="admin-view">
    <NavigationView />
    <div class="page settings-page">
      <div class="page-header">
        <div>
          <div class="page-title">การตั้งค่า</div>
        </div>
        <div class="header-actions">
          <v-btn text color="primary" :disabled="saving" @click="resetToDefaults">
            <v-icon left>mdi-restore</v-icon>คืนค่าเริ่มต้น
          </v-btn>
          <v-btn color="secondary" depressed large class="cta-button" :loading="saving" :disabled="!dirty" @click="save">
            <v-icon left>mdi-content-save</v-icon>บันทึก
          </v-btn>
        </div>
      </div>

      <v-alert type="info" text border="left" class="settings-notice">
        การปรับการตั้งค่าทั้งหมด จะมีผลกับผู้ใช้ทุกท่าน หลังกดบันทึก ผู้ใช้จะเห็นการตั้งค่าใหม่เมื่อเปิดเว็บครั้งถัดไป
      </v-alert>

      <!-- คำทักทาย -->
      <v-card class="data-card settings-card">
        <div class="settings-section-title">
          <v-icon color="primary" class="mr-2">mdi-hand-wave-outline</v-icon>คำทักทายหลังเข้าสู่ระบบ
        </div>

        <div class="setting-row">
          <div>
            <div class="setting-label">แสดงคำทักทาย</div>
            <div class="setting-help">เด้งมุมขวาบนหลังเข้าสู่ระบบ 5 วินาที พร้อมจำนวนประชุมของผู้ใช้วันนี้</div>
          </div>
          <v-switch v-model="form.greeting.enabled" color="primary" inset hide-details></v-switch>
        </div>

        <div :class="{ 'setting-block--disabled': !form.greeting.enabled }">
          <div class="setting-row">
            <div>
              <div class="setting-label">หางเสียง</div>
              <div class="setting-help">ต่อท้ายคำทักทายหลัก</div>
            </div>
            <v-btn-toggle v-model="form.greeting.particle" mandatory dense color="primary">
              <v-btn value="" small>ไม่มี</v-btn>
              <v-btn value="ค่ะ" small>ค่ะ</v-btn>
              <v-btn value="ครับ" small>ครับ</v-btn>
            </v-btn-toggle>
          </div>

          <div class="slot-table">
            <div class="slot-head">
              <span>ช่วงเวลา</span><span>คำทักทายหลัก</span><span>ข้อความเพิ่ม (ไม่ใส่ก็ได้)</span>
            </div>
            <div v-for="slot in slotLabels" :key="slot.key" class="slot-row">
              <div class="slot-name">
                <div>{{ slot.label }}</div>
                <div class="slot-time">{{ slot.time }}</div>
              </div>
              <v-text-field
                v-model="form.greeting.slots[slot.key].hello"
                :placeholder="defaults.greeting.slots[slot.key].hello"
                :rules="[v => !!(v && v.trim()) || 'ต้องมีคำทักทาย']"
                maxlength="60"
                outlined dense hide-details="auto"
              ></v-text-field>
              <v-text-field
                v-model="form.greeting.slots[slot.key].extra"
                maxlength="60"
                outlined dense hide-details
              ></v-text-field>
              <div class="slot-preview">{{ preview(slot.key) }}</div>
            </div>
          </div>
        </div>
      </v-card>

      <!-- ปฏิทินและลูกเล่น -->
      <v-card class="data-card settings-card">
        <div class="settings-section-title">
          <depa-bird :size="20" class="mr-2"></depa-bird>ปฏิทินและลูกเล่น
        </div>

        <div class="setting-row">
          <div>
            <div class="setting-label">มุมมองปฏิทินเริ่มต้น</div>
            <div class="setting-help">ตอนเปิดหน้าปฏิทินการประชุม</div>
          </div>
          <v-btn-toggle v-model="form.calendarView" mandatory dense color="primary">
            <v-btn value="month" small>เดือน</v-btn>
            <v-btn value="week" small>สัปดาห์</v-btn>
            <v-btn value="day" small>วัน</v-btn>
          </v-btn-toggle>
        </div>

        <div class="setting-row">
          <div>
            <div class="setting-label">แอนิเมชัน</div>
            <div class="setting-help">นก depa บินวนตอนรอสร้างการประชุมและตอนลงทะเบียน (เสร็จแล้ววนครบรอบแล้วจางหาย) ปุ่มขยับเวลาชี้ จุดกะพริบของการประชุมที่กำลังประชุม (เครื่องที่ตั้ง "ลดการเคลื่อนไหว" จะปิดให้อัตโนมัติ)</div>
          </div>
          <v-switch v-model="form.animations" color="primary" inset hide-details></v-switch>
        </div>

      </v-card>

      <div v-if="dirty" class="unsaved-note">มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก</div>
    </div>

    <v-snackbar v-model="snack.show" :timeout="2500" :color="snack.color" bottom>{{ snack.text }}</v-snackbar>
    <FooterView></FooterView>
  </div>
</template>

<script>
import NavigationView from '../components/Navigation.vue';
import FooterView from '../components/Footer.vue';
import DepaBird from '../components/DepaBird.vue';
import {
  settings, ensureSettings, saveSettings, cloneSettings, DEFAULT_SETTINGS, SLOT_LABELS,
} from '@/fun/settings';
import { buildGreeting } from '@/fun/greeting';
import { mapGetters } from 'vuex';

export default {
  name: 'SettingsView',
  components: { NavigationView, FooterView, DepaBird },
  data() {
    return {
      // แก้ในสำเนา กด "บันทึก" ถึงจะส่งไป backend
      form: cloneSettings(settings),
      original: JSON.stringify(settings),
      defaults: DEFAULT_SETTINGS,
      slotLabels: SLOT_LABELS,
      saving: false,
      snack: { show: false, text: '', color: 'primary' },
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    dirty() {
      return JSON.stringify(this.form) !== this.original;
    },
  },
  async mounted() {
    await ensureSettings(true);
    this.form = cloneSettings(settings);
    this.original = JSON.stringify(settings);
  },
  methods: {
    preview(key) {
      const slot = this.form.greeting.slots[key];
      const name = this.getUser && this.getUser.name;
      return buildGreeting({ hello: slot.hello || this.defaults.greeting.slots[key].hello, extra: slot.extra },
        this.form.greeting.particle, name).text;
    },
    resetToDefaults() {
      this.form = cloneSettings(DEFAULT_SETTINGS);
    },
    async save() {
      const emptySlot = this.slotLabels.find(s => !(this.form.greeting.slots[s.key].hello || '').trim());
      if (emptySlot) {
        this.snack = { show: true, text: `กรุณาใส่คำทักทายช่วง "${emptySlot.label}"`, color: 'error' };
        return;
      }
      this.saving = true;
      try {
        const saved = await saveSettings(this.form);
        this.form = cloneSettings(saved);
        this.original = JSON.stringify(saved);
        this.snack = { show: true, text: 'บันทึกแล้ว ผู้ใช้ทุกคนจะเห็นการตั้งค่าใหม่', color: 'primary' };
      } catch (e) {
        const reason = e.response && e.response.data && e.response.data.result;
        this.snack = { show: true, text: `บันทึกไม่สำเร็จ${reason ? `: ${reason}` : ''}`, color: 'error' };
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.settings-page {
  max-width: 1000px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.settings-notice {
  margin-bottom: 20px;
  font-size: 15px;
}

.settings-card {
  margin-bottom: 20px;
  padding-bottom: 8px;
}

.settings-section-title {
  display: flex;
  align-items: center;
  padding: 18px 20px 10px;
  color: #0C2F53;
  font-size: 17px;
  font-weight: 700;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #F0F3F7;
}

.setting-block--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.setting-label {
  color: #2B3A4D;
  font-size: 16px;
  font-weight: 600;
}

.setting-help {
  color: #5B6B7F;
  font-size: 14px;
}

.setting-row .v-input--switch {
  margin-top: 0;
  padding-top: 0;
}

.slot-table {
  padding: 8px 20px 12px;
  border-top: 1px solid #F0F3F7;
}

.slot-head,
.slot-row {
  display: grid;
  grid-template-columns: 130px 1fr 1fr;
  gap: 12px;
  align-items: center;
}

.slot-head {
  padding: 6px 0;
  color: #5B6B7F;
  font-size: 13px;
  font-weight: 600;
}

.slot-row {
  padding: 8px 0;
}

.slot-name {
  color: #2B3A4D;
  font-weight: 600;
}

.slot-time {
  color: #8A97A8;
  font-size: 12px;
  font-weight: 400;
}

.slot-preview {
  grid-column: 2 / span 2;
  margin-top: -2px;
  color: #5B6B7F;
  font-size: 13px;
}

.slot-preview::before {
  content: 'ตัวอย่าง: ';
  color: #8A97A8;
}

.unsaved-note {
  text-align: right;
  color: #B26A00;
  font-size: 14px;
}

@media (max-width: 700px) {
  .setting-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .slot-head {
    display: none;
  }

  .slot-head,
  .slot-row {
    grid-template-columns: 1fr;
  }

  .slot-preview {
    grid-column: auto;
  }
}
</style>
